"use client";

import React from "react";
import { Upload, Microscope, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Expert Consultation",
  description: "Begin with a clinical evaluation of your sleep history and airway health with our specialized team.",
  icon: Video
}, {
  title: "Precision 3D Scanning",
  description: "We utilize advanced digital impressions to create a perfect 3D map of your bite without messy molds.",
  icon: Microscope
}, {
  title: "Custom Fitting",
  description: "Receive your bespoke oral appliance, custom-calibrated for immediate relief and restorative sleep.",
  icon: Upload
}];
const faqs = [{
  question: "Do I need a prior sleep study?",
  answer: "Yes, a diagnostic sleep study is necessary to determine the severity of your sleep apnea and ensure that oral appliance therapy is the most effective treatment for your specific needs."
}, {
  question: "Is the consultation covered by insurance?",
  answer: "Most medical insurance plans cover the initial consultation. Our team will help you verify your benefits and navigate the coverage process for your treatment."
}, {
  question: "How long until I receive my appliance?",
  answer: "After your 3D scan, your custom appliance is typically fabricated and ready for fitting within 2 to 3 weeks, ensuring a perfect, medical-grade result."
}, {
  question: "Is oral appliance therapy effective?",
  answer: "Absolutely. Clinical research demonstrates that custom-fitted oral appliances are a highly effective alternative to CPAP for patients with mild to moderate obstructive sleep apnea."
}];
export default function EvaluationProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="142673" id="the-treatment-journey" className="bg-background text-foreground py-24 sm:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-10 pointer-events-none">
        <div className="absolute inset-0 bg-secondary/30 blur-[100px] rounded-full mix-blend-multiply" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-2xl text-center mb-16 lg:mb-24">
          <h2 className="text-base/7 font-bold text-secondary tracking-widest uppercase">
            The Treatment Journey
          </h2>
          <p className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl text-primary font-sans">
            Your Path to Restful Sleep
          </p>
          <p className="mt-6 text-lg/8 text-muted-foreground">
            Our clinical process is designed to move you from exhausted to energized with precision technology and personalized care.
          </p>
        </div>

        {}
        <div ref={ref} className="relative mx-auto max-w-5xl mb-24">
          {}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            {}
            <div className="hidden md:block absolute top-12 left-[16.66%] right-[16.66%] h-0.5">
              <div className="absolute inset-0 border-t-2 border-dashed border-muted/50" />
              <div className={cn("absolute inset-0 bg-secondary transition-transform duration-1500 ease-in-out origin-left", isVisible ? "scale-x-100" : "scale-x-0")} />
            </div>
            {}
            <div className="md:hidden absolute top-24 bottom-24 left-1/2 -translate-x-1/2 w-0.5">
              <div className="absolute inset-0 border-l-2 border-dashed border-muted/50" />
              <div className={cn("absolute inset-0 bg-secondary transition-transform duration-1500 ease-in-out origin-top", isVisible ? "scale-y-100" : "scale-y-0")} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative z-10">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={step.title} data-index={i} className="flex flex-col items-center text-center group">
                  <div className={cn("flex size-24 items-center justify-center rounded-full bg-card border-2 border-border shadow-md transition-all duration-500 relative", isVisible && "border-secondary/50 shadow-secondary/10 shadow-xl")}>
                    <Icon className={cn("size-10 text-muted-foreground transition-colors duration-500", isVisible && "text-secondary")} strokeWidth={1.5} />
                    {}
                    <div className="absolute -top-2 -right-2 flex size-8 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-sm font-bold shadow-sm">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="mt-6 text-xl font-bold text-primary">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-base/7 text-muted-foreground max-w-xs">
                    {step.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="flex justify-center mb-24">
          <Button size="lg" className="rounded-full px-10 h-14 text-base bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg shadow-secondary/20 transition-all hover:scale-105" asChild>
            <Link to="#">
              Book Free Consultation
            </Link>
          </Button>
        </div>

        {}
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold tracking-tight sm:text-3xl text-primary font-sans">
              Frequently Asked Questions
            </h3>
            <p className="mt-4 text-muted-foreground">
              Everything you need to know about starting your journey to better sleep.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                <AccordionTrigger className="text-left text-base font-semibold text-primary hover:text-secondary hover:no-underline py-5 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base/7 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}